import { Logger } from '~~/helpers/logger';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { ALLOWED_METHODS } from '~~/types/expondoMiddleware';
import type { ProductListingFilterState } from '~~/types/productListing/ProductListingFilterState';
import type { CtSearchProductsResult } from '~~/types/productListing/CtSearchProductsResult';
import type { CtSearchProductsParams } from '~~/types/productListing/CtSearchProductsParams';

// Keeps no state: the caller renders the useAsyncData result, which drops responses of superseded requests.
export function useCtProductSearch() {

  const apiFetch = useApiFetch();

  async function search(
    params: { categoryId?: string; filterState: ProductListingFilterState; signal?: AbortSignal },
  ): Promise<CtSearchProductsResult> {

    Logger.debug('useCtProductSearch/search', params);

    const { categoryId, filterState, signal } = params;
    const endpoint = `${EXPONDO_MIDDLEWARE_ROUTES.CT.ROOT}${EXPONDO_MIDDLEWARE_ROUTES.CT.SEARCH_PRODUCTS}`;
    const fullPath = `${EXPONDO_MIDDLEWARE_ROUTES.ROOT}${endpoint}`;

    try {

      return await apiFetch<CtSearchProductsResult>(fullPath, {
        method: ALLOWED_METHODS.post,
        body: {
          categoryId,
          query: filterState.query || undefined,
          limit: filterState.hitsPerPage,
          offset: (filterState.page - 1) * filterState.hitsPerPage,
          sort: filterState.sortBy,
          filters: {
            refinementList: filterState.refinementList,
            price: filterState.range.price,
            minRating: filterState.ratingMenu.avgRating,
            category: filterState.category,
          },
        } satisfies CtSearchProductsParams,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        signal,
      });

    } catch (err: unknown) {

      if (!signal?.aborted) {

        Logger.error('useCtProductSearch/search', err);

      }

      throw err;

    }

  }

  async function count(): Promise<number> {

    const endpoint = `${EXPONDO_MIDDLEWARE_ROUTES.CT.ROOT}${EXPONDO_MIDDLEWARE_ROUTES.CT.COUNT_PRODUCTS}`;
    const { total } = await apiFetch<{ total: number }>(`${EXPONDO_MIDDLEWARE_ROUTES.ROOT}${endpoint}`, {
      method: ALLOWED_METHODS.post,
      credentials: 'same-origin',
    });

    return total;

  }

  return { search, count };

}
